import type { SormFilterSpec } from '@moment/sorm-grid-ui/saved-views'

import { IsoDateStr, RelativeDate } from '@moment/domain-types'
import { z } from 'zod'

/** A filter a link applies: a Security field and the comparison, which the grid labels. */
export type MarketplaceFilterSpec = { field: string } & SormFilterSpec

/** Opens a blank, unsaved view that applies only the link's filters, across every asset class. */
export const MARKETPLACE_NEW_VIEW_ID = 'new'

/** Where a link into the marketplace lands: a view, with filters on top of it as unsaved changes. */
export interface MarketplaceTarget {
  /** A built-in or saved view id, or {@link MARKETPLACE_NEW_VIEW_ID}; `null` is the default (Treasury) view. */
  viewId: string | null
  filters: readonly MarketplaceFilterSpec[]
}

const filterField = z.string().min(1)

const MARKETPLACE_FILTER_SPECS = z.array(
  z.union([
    z.object({
      field: filterField,
      kind: z.literal('boolean'),
      value: z.boolean(),
    }),
    z.object({
      field: filterField,
      kind: z.literal('categorical'),
      values: z.array(z.string()).min(1),
    }),
    z.object({
      field: filterField,
      kind: z.literal('numeric'),
      operator: z.enum(['gte', 'gt', 'lte', 'lt', 'eq', 'neq']),
      value: z.number(),
    }),
    z.object({
      field: filterField,
      kind: z.literal('numeric'),
      operator: z.literal('between'),
      min: z.number(),
      max: z.number(),
    }),
    z.object({
      field: filterField,
      kind: z.literal('ordinal'),
      operator: z.enum(['gte', 'lte']),
      value: z.string().min(1),
    }),
    z.object({
      field: filterField,
      kind: z.literal('ordinal'),
      operator: z.literal('between'),
      min: z.string().min(1),
      max: z.string().min(1),
    }),
    z.object({
      field: filterField,
      kind: z.literal('date'),
      dateOp: z.enum(['gte', 'lte', 'on']),
      iso: IsoDateStr,
      relativeDay: RelativeDate.optional(),
    }),
    z.object({
      field: filterField,
      kind: z.literal('date'),
      dateOp: z.literal('between'),
      start: IsoDateStr,
      end: IsoDateStr,
      relativeStart: RelativeDate.optional(),
      relativeEnd: RelativeDate.optional(),
    }),
  ]),
)

export function serializeMarketplaceFilters(
  filters: readonly MarketplaceFilterSpec[],
): string {
  return JSON.stringify(filters)
}

/** Reads back `serializeMarketplaceFilters`; no filters for a missing or malformed value, like a hand-edited URL. */
export function parseMarketplaceFilters(
  value: string | null,
): MarketplaceFilterSpec[] {
  if (value === null) {
    return []
  }
  let json: unknown
  try {
    json = JSON.parse(value)
  } catch {
    return []
  }
  const parsed = MARKETPLACE_FILTER_SPECS.safeParse(json)
  return parsed.success ? parsed.data : []
}
